import { invariant } from '@e-socna/utils';
import { getFragmentData, ThirdPartyTypeEnum } from '@e-socna/data-layer';
import type { FragmentType, Id } from '@e-socna/data-layer';

import { Wizard } from '../../components/Wizard';

import { FormVariant } from '../enums/variant.enum';

import { useOrganizationInitialValuesFactory } from './hooks/useOrganizationInitialValuesFactory.hook';
import { ThirdPartyForm_Organization_Fragment } from './fragments/organization.fragment';
import { useThirdPartySteps } from './hooks/useSteps.hook';
import { useOrganizationSubmit } from './hooks/useOrganizationSubmit.hook';
import { OrganizationSchema } from './schemas/organization.schema';
import type { ThirdPartyFormTab } from './enums/tab.enum';
import type { ThirdPartyForm_Organization } from './fragments/organization.fragment';
import type { OrganizationMutationResult } from './types/organization-mutation-result.type';

interface Props {
  readonly askMemoBeforeSubmit?: boolean;
  readonly formVariant: FormVariant;
  readonly initialValues?: FragmentType<
    typeof ThirdPartyForm_Organization_Fragment
  >;
  readonly isInternal: boolean;
  readonly onCancel?: () => void;
  readonly onSuccess?: (result: OrganizationMutationResult) => void;
  readonly organizationId?: Id;
  readonly setMustBlockAction?: (mustBlock: boolean) => void;
  readonly showCancelButton?: boolean;
  readonly stepIdsSubset?: readonly ThirdPartyFormTab[];
  readonly title?: string;
}

export const OrganizationForm = ({
  askMemoBeforeSubmit = true,
  formVariant,
  initialValues: ppInitialValues,
  isInternal,
  onCancel: handleCancel,
  onSuccess: handleSuccess,
  organizationId,
  setMustBlockAction,
  showCancelButton = true,
  stepIdsSubset,
  title,
}: Readonly<Props>) => {
  invariant(
    !!(formVariant !== FormVariant.Modification || organizationId),
    'You must provide an organizationId when formVariant is Modification',
  );

  const pInitialValues = getFragmentData(
    ThirdPartyForm_Organization_Fragment,
    ppInitialValues,
  );

  const { initialValues } = useOrganizationInitialValuesFactory({
    initialValues: pInitialValues,
    isInternal,
  });

  const { handleSubmit } = useOrganizationSubmit({
    formVariant,
    handleSuccess,
    initialValues,
    organizationId,
  });

  const { steps, wizardChildren } =
    useThirdPartySteps<ThirdPartyForm_Organization>({
      isInternal,
      kind: ThirdPartyTypeEnum.Organization,
      stepIdsSubset,
      thirdPartyId: organizationId,
    });

  return (
    <Wizard
      ValidationSchema={OrganizationSchema}
      askMemoBeforeSubmit={askMemoBeforeSubmit}
      formTitle={title}
      formVariant={formVariant}
      initialValues={initialValues}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      setMustBlockAction={setMustBlockAction}
      showCancelButton={showCancelButton}
      steps={steps}
    >
      {wizardChildren}
    </Wizard>
  );
};
